import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../navbarv2/navbar";
import Footer from "../navbarv2/footer";
import logo from "../img/zooE-ticket-logo.png";
import iconlogo from "../img/zooE-ticket-icon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAppStoreIos, faGooglePlay, faLine, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faNewspaper } from "@fortawesome/free-regular-svg-icons";
import { faAward, faTicket, faLeaf, faPaw } from "@fortawesome/free-solid-svg-icons";

/* ── Intersection observer hook for scroll reveals ── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ── Floating leaf particle ── */
function Leaf({ style }) {
  return (
    <div style={{
      position: "absolute",
      fontSize: "clamp(14px, 2vw, 22px)",
      opacity: 0.18,
      pointerEvents: "none",
      userSelect: "none",
      animation: `leafDrift ${8 + Math.random() * 6}s ease-in-out ${Math.random() * 5}s infinite alternate`,
      ...style,
    }}>🌿</div>
  );
}

/* ── Feature card ── */
function FeatureCard({ icon, title, desc, delay, hue }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      flex: "1 1 220px",
      padding: "36px 28px",
      borderRadius: 24,
      background: "#fff",
      border: "1px solid rgba(34,139,34,.1)",
      boxShadow: "0 4px 24px rgba(34,139,34,.06)",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
      textAlign: "center",
      transform: visible ? "translateY(0)" : "translateY(32px)",
      opacity: visible ? 1 : 0,
      transition: `transform .65s ease ${delay}s, opacity .65s ease ${delay}s, box-shadow .3s`,
      cursor: "default",
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 12px 40px hsl(${hue},60%,40%,.14)`; e.currentTarget.style.transform = "translateY(-6px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(34,139,34,.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{
        width: 72, height: 72, borderRadius: "50%",
        background: `linear-gradient(135deg, hsl(${hue},65%,92%), hsl(${hue},65%,85%))`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 28, color: `hsl(${hue},55%,38%)`,
        boxShadow: `0 4px 16px hsl(${hue},60%,60%,.2)`,
      }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div style={{ fontFamily: "'Prompt', sans-serif", fontSize: 18, fontWeight: 700, color: "#1a3320" }}>{title}</div>
      <div style={{ fontSize: 14, color: "#5a7060", lineHeight: 1.7 }}>{desc}</div>
    </div>
  );
}

/* ── Download button ── */
function AppBtn({ icon, label, sub, href, color }) {
  const [hov, setHov] = useState(false);
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      <div style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 28px",
        borderRadius: 16,
        background: hov ? color : "#fff",
        border: `2px solid ${color}`,
        boxShadow: hov ? `0 8px 28px ${color}55` : "0 2px 12px rgba(0,0,0,.06)",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        transition: "all .25s ease",
        minWidth: 180,
      }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: 26, color: hov ? "#fff" : color }} />
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 11, color: hov ? "rgba(255,255,255,.75)" : "#888", letterSpacing: ".08em" }}>ดาวน์โหลดบน</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: hov ? "#fff" : "#1a3320", fontFamily: "'Prompt',sans-serif" }}>{label}</div>
        </div>
      </div>
    </a>
  );
}

/* ── Contact card ── */
function ContactCard({ icon, label, href, color, qr, btnLabel, delay }) {
  const [ref, visible] = useReveal();
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref} style={{
      flex: "1 1 240px",
      background: "#fff",
      borderRadius: 24,
      padding: "32px 28px",
      border: "1px solid rgba(0,0,0,.07)",
      boxShadow: "0 4px 20px rgba(0,0,0,.06)",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
      transform: visible ? "translateY(0)" : "translateY(32px)",
      opacity: visible ? 1 : 0,
      transition: `transform .6s ease ${delay}s, opacity .6s ease ${delay}s`,
    }}>
      <a href={href} target="_blank" rel="noreferrer"
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ textDecoration: "none", width: "100%" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          padding: "12px 20px", borderRadius: 14,
          background: hov ? color : `${color}15`,
          border: `2px solid ${color}`,
          color: hov ? "#fff" : color,
          fontSize: 15, fontWeight: 700,
          fontFamily: "'Prompt',sans-serif",
          transition: "all .25s",
          boxShadow: hov ? `0 6px 20px ${color}44` : "none",
          transform: hov ? "scale(1.03)" : "scale(1)",
        }}>
          <FontAwesomeIcon icon={icon} fontSize={18} />
          {btnLabel}
        </div>
      </a>
      <div style={{
        borderRadius: 16, overflow: "hidden",
        border: "3px solid #fff",
        boxShadow: `0 4px 20px ${color}33`,
      }}>
        <img src={qr} alt={label} width={130} style={{ display: "block" }} />
      </div>
      <div style={{ fontSize: 13, color: "#888", textAlign: "center" }}>สแกน QR เพื่อติดต่อ</div>
    </div>
  );
}

/* ════════════════════════════════
   MAIN
════════════════════════════════ */
function AboutUs() {
  const [heroRef, heroVisible] = useReveal();
  const [aboutRef, aboutVisible] = useReveal();
  const [appRef, appVisible] = useReveal();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;600;700;800&family=Sarabun:wght@400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        @keyframes leafDrift {
          0%   { transform: translateY(0px) rotate(-8deg) scale(1); }
          50%  { transform: translateY(-18px) rotate(8deg) scale(1.1); }
          100% { transform: translateY(4px) rotate(-4deg) scale(.95); }
        }
        @keyframes heroSlide {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes ripple {
          0%   { transform: scale(1); opacity: .6; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes paw {
          0%,100% { transform: rotate(-8deg) scale(1); }
          50%     { transform: rotate(8deg) scale(1.15); }
        }

        body { background: #f7faf5; }

        .about-page {  color: #2d4a34; }

        .nav-link-home {
          color: rgba(255,255,255,.85)!important;
          text-decoration: none;
          font-weight: 500;
          transition: color .2s;
        }
        .nav-link-home:hover { color: #fff!important; }
      `}</style>

      <div className="about-page">
        <Navbar />

        {/* ══ HERO BANNER ══ */}
        <div style={{
          position: "relative", overflow: "hidden",
          background: "linear-gradient(135deg, #1a4a24 0%, #2d7a3a 45%, #3da84a 100%)",
          padding: "80px 24px 90px",
          textAlign: "center",
        }}>
          {/* Decorative circles */}
          {[
            { w:320, h:320, top:"-120px", right:"-80px", opacity:.08 },
            { w:200, h:200, bottom:"-60px", left:"-60px", opacity:.1 },
            { w:140, h:140, top:"20%", left:"8%", opacity:.06 },
          ].map((c, i) => (
            <div key={i} style={{
              position:"absolute", width:c.w, height:c.h, borderRadius:"50%",
              background:"#fff", top:c.top, bottom:c.bottom, left:c.left, right:c.right,
              opacity:c.opacity, pointerEvents:"none",
            }}/>
          ))}

          {/* Floating leaves */}
          <Leaf style={{ top:"15%", left:"5%" }}/>
          <Leaf style={{ top:"60%", left:"12%" }}/>
          <Leaf style={{ top:"25%", right:"8%" }}/>
          <Leaf style={{ bottom:"20%", right:"5%" }}/>

          {/* Paw icon decoration */}
          <div style={{
            position:"absolute", top:24, right:32, fontSize:40, opacity:.12,
            animation:"paw 3s ease-in-out infinite",
          }}>🐾</div>
          <div style={{
            position:"absolute", bottom:24, left:32, fontSize:32, opacity:.12,
            animation:"paw 4s ease-in-out 1s infinite",
          }}>🌿</div>

          <div ref={heroRef} style={{
            position:"relative", zIndex:2,
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity .7s ease, transform .7s ease",
          }}>
            {/* Breadcrumb */}
            <nav style={{ marginBottom:24 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <Link to="/" className="nav-link-home">Home</Link>
                <span style={{ color:"rgba(255,255,255,.4)", fontSize:12 }}>›</span>
                <span style={{ color:"#a8e6b0", fontWeight:600, fontSize:14 }}>About Us</span>
              </div>
            </nav>

            <h1 style={{
              fontFamily:"'Prompt',sans-serif",
              fontSize:"clamp(36px,7vw,72px)",
              fontWeight:800, color:"#fff",
              lineHeight:1.1, marginBottom:16,
              letterSpacing:"-.02em",
            }}>
              เกี่ยวกับ{" "}
              <span style={{
                background:"linear-gradient(90deg,#a8ffb8,#d4ffde,#a8ffb8)",
                backgroundSize:"200% auto",
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                animation:"shimmer 3s linear infinite",
              }}>
                เรา
              </span>
            </h1>

            <p style={{
              fontSize:"clamp(15px,2.5vw,20px)",
              color:"rgba(255,255,255,.8)",
              maxWidth:500, margin:"0 auto",
              lineHeight:1.8,
            }}>
              ระบบจำหน่ายบัตรสวนสัตว์ออนไลน์<br/>ครบวงจร สะดวก รวดเร็ว ทุกที่ทุกเวลา
            </p>
          </div>

          {/* Bottom wave */}
          <div style={{
            position:"absolute", bottom:-2, left:0, right:0,
            height:60, overflow:"hidden",
          }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width:"100%", height:"100%", display:"block" }}>
              <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#f7faf5"/>
            </svg>
          </div>
        </div>

        {/* ══ ABOUT SECTION ══ */}
        <div style={{ maxWidth:900, margin:"0 auto", padding:"72px 24px 48px", textAlign:"center" }}>
          <div ref={aboutRef} style={{
            opacity: aboutVisible ? 1 : 0,
            transform: aboutVisible ? "translateY(0)" : "translateY(24px)",
            transition:"opacity .7s ease, transform .7s ease",
          }}>
            {/* Logo */}
            <div style={{
              display:"inline-block",
              padding:"24px 40px",
              background:"#fff",
              borderRadius:28,
              boxShadow:"0 8px 40px rgba(34,139,34,.1)",
              marginBottom:40,
              animation:"float 4s ease-in-out infinite",
            }}>
              <img src={logo} alt="Zoo E-Ticket" style={{ maxWidth:220, height:"auto", display:"block" }}/>
            </div>

            <div style={{
              display:"inline-flex", alignItems:"center", gap:8,
              background:"rgba(45,122,58,.08)", border:"1px solid rgba(45,122,58,.2)",
              borderRadius:100, padding:"6px 18px", marginBottom:20,
            }}>
              <FontAwesomeIcon icon={faPaw} style={{ color:"#2d7a3a", fontSize:13 }}/>
              <span style={{ fontSize:13, color:"#2d7a3a", fontWeight:600, letterSpacing:".08em" }}>ZOO E-TICKETS</span>
            </div>

            <h2 style={{
              fontFamily:"'Prompt',sans-serif",
              fontSize:"clamp(28px,5vw,44px)",
              fontWeight:800, color:"#1a3320",
              lineHeight:1.2, marginBottom:24,
            }}>
              Zoo <span style={{ color:"#2d7a3a" }}>E-Tickets</span>
            </h2>

            <p style={{
              fontSize:"clamp(15px,2vw,18px)",
              color:"#4a6652",
              lineHeight:1.9, maxWidth:620, margin:"0 auto",
            }}>
              การจำหน่ายบัตรเข้าชมสวนสัตว์ ผ่านช่องทางออนไลน์<br/>
              ได้ทั้งเว็บแอปพลิเคชันและแอปพลิเคชั่นบนมือถือ<br/>
              <span style={{ color:"#2d7a3a", fontWeight:600 }}>(Web Application & Mobile Application)</span>
            </p>
          </div>
        </div>

        {/* ══ FEATURES ══ */}
        <div style={{
          background:"linear-gradient(180deg,#f7faf5 0%,#eef6ef 100%)",
          padding:"60px 24px",
          position:"relative", overflow:"hidden",
        }}>
          {/* BG dots */}
          <div style={{
            position:"absolute", inset:0, pointerEvents:"none",
            backgroundImage:"radial-gradient(rgba(34,139,34,.12) 1.5px, transparent 1.5px)",
            backgroundSize:"28px 28px",
          }}/>

          <div style={{ maxWidth:960, margin:"0 auto", position:"relative", zIndex:1 }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div style={{
                display:"inline-flex", gap:6, alignItems:"center",
                background:"rgba(45,122,58,.08)", borderRadius:100, padding:"5px 16px",
                fontSize:12, color:"#2d7a3a", fontWeight:700, letterSpacing:".1em",
                marginBottom:14,
              }}>
                <FontAwesomeIcon icon={faLeaf}/>
                ฟีเจอร์หลัก
              </div>
              <h2 style={{
                fontFamily:"'Prompt',sans-serif",
                fontSize:"clamp(24px,4vw,36px)",
                fontWeight:800, color:"#1a3320",
              }}>
                ทำไมต้อง Zoo E-Tickets?
              </h2>
            </div>

            <div style={{ display:"flex", gap:20, flexWrap:"wrap", justifyContent:"center" }}>
              <FeatureCard
                icon={faTicket} hue={120}
                title="รวมบัตรเข้าชม"
                desc="ไม่ว่าจะเที่ยวสวนสัตว์ไหนก็ซื้อบัตรเข้าชมได้เลย ครบทุกสวนสัตว์ในที่เดียว"
                delay={0}
              />
              <FeatureCard
                icon={faNewspaper} hue={200}
                title="รวมข่าวสาร"
                desc="สิทธิประโยชน์และความน่าสนใจของสัตว์ที่คุณชื่นชอบ อัปเดตทุกวัน"
                delay={0.12}
              />
              <FeatureCard
                icon={faAward} hue={45}
                title="รวมสิทธิพิเศษ"
                desc="สมัครสมาชิก เพื่อรับสิทธิพิเศษก่อนใคร โปรโมชันสุดคุ้มทุกเดือน"
                delay={0.24}
              />
            </div>
          </div>
        </div>

        {/* ══ APP DOWNLOAD ══ */}
        <div style={{ maxWidth:700, margin:"0 auto", padding:"80px 24px 60px", textAlign:"center" }}>
          <div ref={appRef} style={{
            opacity: appVisible ? 1 : 0,
            transform: appVisible ? "translateY(0)" : "translateY(24px)",
            transition:"opacity .7s ease, transform .7s ease",
          }}>
            <div style={{
              display:"inline-flex", gap:6, alignItems:"center",
              background:"rgba(45,122,58,.08)", borderRadius:100, padding:"5px 16px",
              fontSize:12, color:"#2d7a3a", fontWeight:700, letterSpacing:".1em",
              marginBottom:16,
            }}>
              <FontAwesomeIcon icon={faPaw}/>
              แอปพลิเคชัน
            </div>

            <h2 style={{
              fontFamily:"'Prompt',sans-serif",
              fontSize:"clamp(22px,4vw,34px)",
              fontWeight:800, color:"#1a3320", marginBottom:32,
            }}>
              Zoo E-ticket Application
            </h2>

            {/* App icon */}
            <div style={{
              display:"inline-block",
              background:"#fff",
              borderRadius:32,
              padding:"20px",
              boxShadow:"0 8px 40px rgba(34,139,34,.12)",
              marginBottom:36,
              animation:"float 3.5s ease-in-out .5s infinite",
              position:"relative",
            }}>
              <img src={iconlogo} alt="Zoo E-Ticket App" style={{ width:100, height:100, display:"block", borderRadius:20 }}/>
              {/* Ripple effect */}
              {[0,1].map(i => (
                <div key={i} style={{
                  position:"absolute", inset:-16,
                  borderRadius:48,
                  border:"2px solid rgba(45,122,58,.2)",
                  animation:`ripple 2.5s ease-out ${i * 1.25}s infinite`,
                  pointerEvents:"none",
                }}/>
              ))}
            </div>

            {/* Download buttons */}
            <div style={{ display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap", marginBottom:16 }}>
              <AppBtn
                icon={faGooglePlay} label="Google Play" sub="ดาวน์โหลดบน"
                href="https://play.google.com/store/apps/details?id=zoo.ticket.eticket&hl=th&pli=1"
                color="#34a853"
              />
              <AppBtn
                icon={faAppStoreIos} label="App Store" sub="ดาวน์โหลดบน"
                href="https://apps.apple.com/th/app/zooe-ticket/id6740426570?l=th"
                color="#007aff"
              />
            </div>

            <p style={{ fontSize:13, color:"#99aaa0", marginTop:8 }}>รองรับ iOS และ Android</p>
          </div>
        </div>

        {/* ══ CONTACT ══ */}
        <div style={{
          background:"linear-gradient(180deg,#eef6ef,#f7faf5)",
          padding:"60px 24px 80px",
        }}>
          <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center" }}>
            <div style={{
              display:"inline-flex", gap:6, alignItems:"center",
              background:"rgba(45,122,58,.08)", borderRadius:100, padding:"5px 16px",
              fontSize:12, color:"#2d7a3a", fontWeight:700, letterSpacing:".1em",
              marginBottom:16,
            }}>
              <FontAwesomeIcon icon={faLeaf}/>
              ติดต่อเรา
            </div>

            <h2 style={{
              fontFamily:"'Prompt',sans-serif",
              fontSize:"clamp(22px,4vw,34px)",
              fontWeight:800, color:"#1a3320", marginBottom:12,
            }}>
              ช่องทางการติดต่อ
            </h2>
            <p style={{ color:"#6a8870", fontSize:15, marginBottom:40 }}>
              มีคำถามหรือต้องการความช่วยเหลือ? ติดต่อเราได้เลยครับ
            </p>

            <div style={{ display:"flex", gap:20, flexWrap:"wrap", justifyContent:"center" }}>
              <ContactCard
                icon={faLine}
                label="LINE"
                btnLabel="LINE Zoo E-Ticket By @pay"
                href="https://page.line.me/803blqpk"
                color="#06c755"
                qr="https://www.addpay.co.th/service-ui/service-my-zoo-admin/live-img/line.png"
                delay={0}
              />
              <ContactCard
                icon={faFacebook}
                label="Facebook"
                btnLabel="Facebook Zoo E-Ticket"
                href="https://www.facebook.com/p/Zoo-E-ticket-by-Addpay-61566554854930/"
                color="#1877f2"
                qr="https://www.addpay.co.th/service-ui/service-my-zoo-admin/live-img/fackbook.png"
                delay={0.12}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default AboutUs;